import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const ITEM_CATEGORIES = [
    "Clothing",
    "Furniture",
    "Books",
    "Homeware",
    "Toys",
    "Electronics",
    "Kitchenware",
    "Shoes",
    "Jewelry",
    "Linen",
    "Tools",
    "Hardware",
    "Media",
    "Fill-A-Bag",
];

const UPDATE_ITEMS_KEY =
    "Nfe7Hi8ae0Cf-PtkdPl3y-R545bpQGEFsryFo5liPJeEAzFufmvlkA==";

const GET_SHOP_KEY =
    "TxEuYhVjo9rIiMlI2EMr8gRFPPRejKrTeKGMbTXwQ--3AzFuHMjRzQ==";

const GET_SHOP_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopById";

const UPDATE_ITEMS_API =
    "https://oppiesfunc.azurewebsites.net/api/updateShopItemsOffered";

const MAX_CUSTOM_LENGTH = 40;

export default function ItemsOfferedTab({
    shop,
    userEmail,
}) {

    /*
     * Selected shop / branch.
     *
     * Normal account:
     *      their own shopId
     *
     * Manager:
     *      whichever shop is selected in Dashboard
     */

    const shopId =
        shop?.shopId || "";


    /*
     * Actual authenticated Portal account.
     *
     * IMPORTANT:
     * This stays as the manager's email even when they switch
     * between branches.
     *
     * Dashboard does not overwrite localStorage.shopEmail
     * during manager shop switching, so the fallback is safe
     * for the current Portal.
     */

    const authenticatedEmail =
        (
            userEmail ||
            localStorage.getItem(
                "shopEmail"
            ) ||
            ""
        )
            .trim()
            .toLowerCase();

    const [
        selectedItems,
        setSelectedItems,
    ] = useState([]);

    const [
        savedItems,
        setSavedItems,
    ] = useState([]);

    const [
        customCategory,
        setCustomCategory,
    ] = useState("");

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        status,
        setStatus,
    ] = useState({
        type: "",
        message: "",
    });

    /* =========================================================
       STATUS
    ========================================================= */

    const showStatus = (
        type,
        message
    ) => {
        setStatus({
            type,
            message,
        });
    };

    /* =========================================================
       NORMALISE ITEMS
    ========================================================= */

    const normaliseItems = (
        items
    ) => {
        if (
            !Array.isArray(
                items
            )
        ) {
            return [];
        }

        const unique = [];

        items
            .filter(
                (item) =>
                    typeof item ===
                    "string"
            )
            .map((item) =>
                item.trim()
            )
            .filter(Boolean)
            .forEach((item) => {
                const exists =
                    unique.some(
                        (existing) =>
                            existing.toLowerCase() ===
                            item.toLowerCase()
                    );

                if (!exists) {
                    unique.push(
                        item
                    );
                }
            });

        return unique;
    };

    /* =========================================================
       LOAD SHOP
    ========================================================= */

    const fetchShop =
        useCallback(
            async () => {
                if (!shopId) {
                    setLoading(
                        false
                    );

                    showStatus(
                        "error",
                        "Your shop ID is unavailable. Please sign in again."
                    );

                    return;
                }

                try {
                    setLoading(
                        true
                    );

                    /*
 * Clear the previous branch while the new selected
 * shop loads.
 *
 * This prevents Shop A categories briefly appearing
 * after a manager switches to Shop B.
 */

                    setStatus({
                        type: "",
                        message: "",
                    });

                    setSelectedItems(
                        []
                    );

                    setSavedItems(
                        []
                    );

                    setCustomCategory(
                        ""
                    );

                    const res =
                        await axios.get(
                            GET_SHOP_API,
                            {
                                params: {
                                    shopId,
                                    code: GET_SHOP_KEY,
                                },
                            }
                        );

                    const existing =
                        normaliseItems(
                            res.data
                                ?.itemsOffered
                        );

                    setSelectedItems(
                        existing
                    );

                    setSavedItems(
                        existing
                    );
                } catch (err) {
                    console.error(
                        "Failed to load shop:",
                        err?.response
                            ?.data ||
                        err?.message ||
                        err
                    );

                    showStatus(
                        "error",
                        "We couldn't load your current item categories."
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },
            [shopId]
        );

    useEffect(() => {
        fetchShop();
    }, [fetchShop]);

    /* =========================================================
       CUSTOM CATEGORIES
    ========================================================= */

    const customItems =
        useMemo(() => {
            const defaultsLower =
                ITEM_CATEGORIES.map(
                    (item) =>
                        item.toLowerCase()
                );

            return Array.from(
                new Set([
                    ...savedItems,
                    ...selectedItems,
                ])
            ).filter(
                (item) =>
                    !defaultsLower.includes(
                        item.toLowerCase()
                    )
            );
        }, [
            savedItems,
            selectedItems,
        ]);

    /* =========================================================
       DISPLAYED CATEGORIES
    ========================================================= */

    const displayedCategories =
        useMemo(() => {
            return normaliseItems([
                ...ITEM_CATEGORIES,
                ...customItems,
            ]);
        }, [customItems]);

    /* =========================================================
       DIRTY STATE
    ========================================================= */

    const hasChanges =
        useMemo(() => {
            const current =
                [...selectedItems]
                    .map(
                        (item) =>
                            item.toLowerCase()
                    )
                    .sort();

            const saved =
                [...savedItems]
                    .map(
                        (item) =>
                            item.toLowerCase()
                    )
                    .sort();

            if (
                current.length !==
                saved.length
            ) {
                return true;
            }

            return current.some(
                (
                    item,
                    index
                ) =>
                    item !==
                    saved[index]
            );
        }, [
            selectedItems,
            savedItems,
        ]);

    /* =========================================================
       TOGGLE ITEM
    ========================================================= */

    const toggleItem = (
        item
    ) => {
        if (saving) {
            return;
        }

        setSelectedItems(
            (current) => {
                const exists =
                    current.some(
                        (existing) =>
                            existing.toLowerCase() ===
                            item.toLowerCase()
                    );

                if (exists) {
                    return current.filter(
                        (existing) =>
                            existing.toLowerCase() !==
                            item.toLowerCase()
                    );
                }

                return [
                    ...current,
                    item,
                ];
            }
        );

        if (status.message) {
            setStatus({
                type: "",
                message: "",
            });
        }
    };

    /* =========================================================
       ADD CUSTOM CATEGORY
    ========================================================= */

    const addCustomCategory =
        () => {
            if (saving) {
                return;
            }

            const clean =
                customCategory
                    .trim()
                    .replace(
                        /\s+/g,
                        " "
                    );

            if (!clean) {
                showStatus(
                    "error",
                    "Enter a category name first."
                );

                return;
            }

            if (
                clean.length >
                MAX_CUSTOM_LENGTH
            ) {
                showStatus(
                    "error",
                    `Custom categories can be up to ${MAX_CUSTOM_LENGTH} characters long.`
                );

                return;
            }

            const existing =
                displayedCategories.find(
                    (item) =>
                        item.toLowerCase() ===
                        clean.toLowerCase()
                );

            if (existing) {
                const alreadySelected =
                    selectedItems.some(
                        (item) =>
                            item.toLowerCase() ===
                            existing.toLowerCase()
                    );

                if (
                    alreadySelected
                ) {
                    showStatus(
                        "info",
                        `"${existing}" is already selected.`
                    );
                } else {
                    setSelectedItems(
                        (current) => [
                            ...current,
                            existing,
                        ]
                    );

                    showStatus(
                        "success",
                        `"${existing}" has been selected. Save your changes when ready.`
                    );
                }

                setCustomCategory(
                    ""
                );

                return;
            }

            setSelectedItems(
                (current) => [
                    ...current,
                    clean,
                ]
            );

            setCustomCategory(
                ""
            );

            showStatus(
                "success",
                `"${clean}" has been added. Save your changes when ready.`
            );
        };

    const handleCustomKeyDown =
        (event) => {
            if (
                event.key ===
                "Enter"
            ) {
                event.preventDefault();

                addCustomCategory();
            }
        };

    /* =========================================================
       REMOVE CUSTOM CATEGORY
    ========================================================= */

    const removeCustomCategory =
        (item) => {
            if (saving) {
                return;
            }

            setSelectedItems(
                (current) =>
                    current.filter(
                        (existing) =>
                            existing.toLowerCase() !==
                            item.toLowerCase()
                    )
            );

            setSavedItems(
                (current) =>
                    current
            );

            showStatus(
                "info",
                `"${item}" has been removed. Save your changes to update your shop.`
            );
        };

    /* =========================================================
       SELECT ALL / CLEAR
    ========================================================= */

    const selectAll = () => {
        setSelectedItems(
            displayedCategories
        );

        showStatus(
            "info",
            "All categories selected. Save your changes when ready."
        );
    };

    const clearAll = () => {
        setSelectedItems([]);

        showStatus(
            "info",
            "All categories cleared. Save your changes to update your shop profile."
        );
    };

    /* =========================================================
       RESET
    ========================================================= */

    const resetChanges =
        () => {
            setSelectedItems([
                ...savedItems,
            ]);

            setCustomCategory(
                ""
            );

            showStatus(
                "info",
                "Unsaved changes have been reset."
            );
        };

    /* =========================================================
       SAVE
    ========================================================= */

    const handleSave =
        async () => {

            if (
                !shopId ||
                saving
            ) {

                return;
            }


            /*
             * We need the identity of the account actually logged
             * into the Portal.
             *
             * This is separate from the shop currently selected by
             * an organisation manager.
             */

            if (
                !authenticatedEmail
            ) {

                showStatus(
                    "error",
                    "Your account email is unavailable. Please sign in again."
                );

                return;
            }


            const itemsToSave =
                normaliseItems(
                    selectedItems
                );


            try {

                setSaving(
                    true
                );


                showStatus(
                    "info",
                    "Saving your item categories..."
                );


                /*
                 * =================================================
                 * MANAGER-AWARE PAYLOAD
                 * =================================================
                 *
                 * shopId:
                 *      selected shop / branch
                 *
                 * itemsOffered:
                 *      existing OPPIES array
                 *
                 * userEmail:
                 *      account actually logged into Portal
                 */

                await axios.post(
                    `${UPDATE_ITEMS_API}?code=${UPDATE_ITEMS_KEY}`,
                    {
                        shopId,

                        itemsOffered:
                            itemsToSave,

                        userEmail:
                            authenticatedEmail,
                    }
                );


                setSelectedItems(
                    itemsToSave
                );


                setSavedItems(
                    itemsToSave
                );


                showStatus(
                    "success",
                    "Items offered updated successfully."
                );


                console.log(
                    "✅ Items Offered saved:",
                    {
                        shopId,

                        userEmail:
                            authenticatedEmail,

                        itemsOffered:
                            itemsToSave,
                    }
                );


            } catch (err) {

                console.error(
                    "Save error:",
                    err?.response
                        ?.data ||
                    err?.message ||
                    err
                );


                /*
                 * Specific manager/owner permission failure.
                 */

                if (
                    err?.response
                        ?.status ===
                    403
                ) {

                    showStatus(
                        "error",
                        err?.response
                            ?.data
                            ?.error ||
                        "You are not authorised to manage this shop."
                    );

                    return;
                }


                showStatus(
                    "error",
                    err?.response
                        ?.data
                        ?.error ||
                    "We couldn't update your item categories. Please try again."
                );


            } finally {

                setSaving(
                    false
                );
            }
        };

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <>
            <style>
                {
                    itemsOfferedStyles
                }
            </style>

            <div className="items-page">

                {/* ============================================
                    INTRO
                ============================================ */}

                <section className="items-intro">

                    <div className="items-eyebrow">
                        Items Offered
                    </div>

                    <h1>
                        Tell shoppers what
                        they can find
                    </h1>

                    <p>
                        Select the types of
                        items your Op Shop
                        usually stocks. You
                        can also add your own
                        categories for items
                        unique to your shop.
                    </p>

                </section>

                {/* ============================================
                    STATUS
                ============================================ */}

                {status.message ? (
                    <div
                        className={`items-status ${status.type
                                ? `items-status-${status.type}`
                                : ""
                            }`}
                    >

                        <div className="items-status-icon">
                            {status.type ===
                                "success"
                                ? "✓"
                                : status.type ===
                                    "error"
                                    ? "!"
                                    : "•"}
                        </div>

                        <span>
                            {
                                status.message
                            }
                        </span>

                    </div>
                ) : null}

                {/* ============================================
                    MAIN PANEL
                ============================================ */}

                <section className="items-panel">

                    <div className="items-panel-header">

                        <div>

                            <div className="items-panel-eyebrow">
                                Shop Categories
                            </div>

                            <h2>
                                What do you offer?
                            </h2>

                            <p>
                                Choose every
                                category that
                                shoppers can
                                normally expect
                                to find in your
                                shop.
                            </p>

                        </div>

                        <div className="items-header-count">

                            <strong>
                                {
                                    selectedItems.length
                                }
                            </strong>

                            <span>
                                selected
                            </span>

                        </div>

                    </div>

                    {loading ? (

                        <div className="items-loading">

                            <div className="items-spinner" />

                            <span>
                                Loading your
                                shop categories...
                            </span>

                        </div>

                    ) : (

                        <>

                            {/* ==================================
                                QUICK ACTIONS
                            ================================== */}

                            <div className="items-toolbar">

                                <div>

                                    <strong>
                                        Select your stock
                                    </strong>

                                    <span>
                                        Tap a category
                                        to add or
                                        remove it.
                                    </span>

                                </div>

                                <div className="items-toolbar-buttons">

                                    <button
                                        type="button"
                                        onClick={
                                            selectAll
                                        }
                                        disabled={
                                            saving
                                        }
                                    >
                                        Select All
                                    </button>

                                    <button
                                        type="button"
                                        onClick={
                                            clearAll
                                        }
                                        disabled={
                                            saving ||
                                            selectedItems.length ===
                                            0
                                        }
                                    >
                                        Clear
                                    </button>

                                </div>

                            </div>

                            {/* ==================================
                                CATEGORY GRID
                            ================================== */}

                            <div className="items-grid">

                                {displayedCategories.map(
                                    (
                                        item
                                    ) => {
                                        const isActive =
                                            selectedItems.some(
                                                (
                                                    selected
                                                ) =>
                                                    selected.toLowerCase() ===
                                                    item.toLowerCase()
                                            );

                                        const isCustom =
                                            !ITEM_CATEGORIES.some(
                                                (
                                                    standard
                                                ) =>
                                                    standard.toLowerCase() ===
                                                    item.toLowerCase()
                                            );

                                        return (
                                            <button
                                                key={
                                                    item
                                                }
                                                type="button"
                                                className={`item-card ${isActive
                                                        ? "item-card-active"
                                                        : ""
                                                    }`}
                                                onClick={() =>
                                                    toggleItem(
                                                        item
                                                    )
                                                }
                                                disabled={
                                                    saving
                                                }
                                                aria-pressed={
                                                    isActive
                                                }
                                            >

                                                <div className="item-card-check">

                                                    {isActive ? (
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            width="14"
                                                            height="14"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="m5 12 4 4L19 6" />
                                                        </svg>
                                                    ) : (
                                                        <span />
                                                    )}

                                                </div>

                                                <div className="item-card-copy">

                                                    <div className="item-card-title-line">

                                                        <strong>
                                                            {
                                                                item
                                                            }
                                                        </strong>

                                                        {isCustom ? (
                                                            <span className="item-custom-badge">
                                                                Custom
                                                            </span>
                                                        ) : null}

                                                    </div>

                                                    <span>
                                                        {isActive
                                                            ? "Included"
                                                            : "Not selected"}
                                                    </span>

                                                </div>

                                            </button>
                                        );
                                    }
                                )}

                            </div>

                            {/* ==================================
                                CUSTOM CATEGORY
                            ================================== */}

                            <div className="items-custom-section">

                                <div className="items-custom-copy">

                                    <div className="items-custom-icon">

                                        <svg
                                            viewBox="0 0 24 24"
                                            width="17"
                                            height="17"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 5v14" />
                                            <path d="M5 12h14" />
                                        </svg>

                                    </div>

                                    <div>

                                        <strong>
                                            Add a custom category
                                        </strong>

                                        <span>
                                            Can't see something
                                            your shop regularly
                                            stocks? Add it here.
                                        </span>

                                    </div>

                                </div>

                                <div className="items-custom-form">

                                    <input
                                        type="text"
                                        value={
                                            customCategory
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setCustomCategory(
                                                event.target
                                                    .value
                                            )
                                        }
                                        onKeyDown={
                                            handleCustomKeyDown
                                        }
                                        maxLength={
                                            MAX_CUSTOM_LENGTH
                                        }
                                        disabled={
                                            saving
                                        }
                                        placeholder="e.g. Craft Supplies"
                                    />

                                    <button
                                        type="button"
                                        onClick={
                                            addCustomCategory
                                        }
                                        disabled={
                                            saving ||
                                            !customCategory.trim()
                                        }
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="14"
                                            height="14"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 5v14" />
                                            <path d="M5 12h14" />
                                        </svg>

                                        Add
                                    </button>

                                </div>

                                <div className="items-custom-limit">
                                    {
                                        customCategory.length
                                    }
                                    {" / "}
                                    {
                                        MAX_CUSTOM_LENGTH
                                    }
                                </div>

                            </div>

                            {/* ==================================
                                CUSTOM CATEGORY LIST
                            ================================== */}

                            {customItems.length >
                                0 ? (
                                <div className="items-custom-list">

                                    <div className="items-custom-list-heading">

                                        <strong>
                                            Your custom categories
                                        </strong>

                                        <span>
                                            {
                                                customItems.length
                                            }
                                        </span>

                                    </div>

                                    <div className="items-custom-tags">

                                        {customItems.map(
                                            (
                                                item
                                            ) => (
                                                <div
                                                    key={
                                                        item
                                                    }
                                                    className="items-custom-tag"
                                                >

                                                    <span>
                                                        {
                                                            item
                                                        }
                                                    </span>

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            removeCustomCategory(
                                                                item
                                                            )
                                                        }
                                                        disabled={
                                                            saving
                                                        }
                                                        aria-label={`Remove ${item}`}
                                                        title={`Remove ${item}`}
                                                    >
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            width="12"
                                                            height="12"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                        >
                                                            <path d="M6 6l12 12" />
                                                            <path d="M18 6 6 18" />
                                                        </svg>
                                                    </button>

                                                </div>
                                            )
                                        )}

                                    </div>

                                </div>
                            ) : null}

                            {/* ==================================
                                INFO
                            ================================== */}

                            <div className="items-tip">

                                <div className="items-tip-icon">

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="17"
                                        height="17"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="9"
                                        />

                                        <path d="M12 11v5" />

                                        <path d="M12 8h.01" />
                                    </svg>

                                </div>

                                <div>

                                    <strong>
                                        Keep your list relevant
                                    </strong>

                                    <span>
                                        Select categories
                                        you regularly stock
                                        rather than occasional
                                        one-off items. Custom
                                        categories are saved
                                        alongside the standard
                                        OPPIES categories.
                                    </span>

                                </div>

                            </div>

                            {/* ==================================
                                ACTIONS
                            ================================== */}

                            <div className="items-actions">

                                <div className="items-change-state">

                                    {hasChanges ? (
                                        <>
                                            <span className="items-change-dot" />

                                            Unsaved changes
                                        </>
                                    ) : (
                                        <>
                                            <span className="items-saved-dot" />

                                            Categories up to date
                                        </>
                                    )}

                                </div>

                                <div className="items-action-buttons">

                                    {hasChanges ? (
                                        <button
                                            type="button"
                                            className="items-reset-button"
                                            onClick={
                                                resetChanges
                                            }
                                            disabled={
                                                saving
                                            }
                                        >
                                            Reset Changes
                                        </button>
                                    ) : null}

                                    <button
                                        type="button"
                                        className="items-save-button"
                                        onClick={
                                            handleSave
                                        }
                                        disabled={
                                            saving ||
                                            !hasChanges
                                        }
                                    >

                                        {saving ? (
                                            <>
                                                <span className="items-button-spinner" />

                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.9"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />

                                                    <path d="M17 21v-8H7v8" />

                                                    <path d="M7 3v5h8" />
                                                </svg>

                                                Save Changes
                                            </>
                                        )}

                                    </button>

                                </div>

                            </div>

                        </>
                    )}

                </section>

            </div>
        </>
    );
}

const itemsOfferedStyles = `

  .items-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  /* ========================================================
     INTRO
  ======================================================== */

  .items-intro {
    margin-bottom: 26px;
  }

  .items-eyebrow,
  .items-panel-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .items-intro h1 {
    max-width: 650px;
    margin: 7px 0;
    color: #17211c;
    font-size:
      clamp(
        26px,
        3vw,
        36px
      );
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .items-intro p {
    max-width: 690px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  /* ========================================================
     STATUS
  ======================================================== */

  .items-status {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 18px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 13px;
    background: #ffffff;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
  }

  .items-status-icon {
    display: grid;
    width: 25px;
    height: 25px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background: #f1f5f9;
    font-weight: 900;
  }

  .items-status-success {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #047857;
  }

  .items-status-success
  .items-status-icon {
    background: #dcfce7;
  }

  .items-status-error {
    border-color: #fecaca;
    background: #fef2f2;
    color: #b91c1c;
  }

  .items-status-error
  .items-status-icon {
    background: #fee2e2;
  }

  .items-status-info {
    border-color: #ded8c9;
    background: #fbf7ed;
    color: #625c4e;
  }

  /* ========================================================
     PANEL
  ======================================================== */

  .items-panel {
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px
      rgba(
        15,
        23,
        42,
        0.035
      );
  }

  .items-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content:
      space-between;
    gap: 20px;
    margin-bottom: 20px;
  }

  .items-panel-header h2 {
    margin: 4px 0 5px;
    color: #17211c;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .items-panel-header p {
    margin: 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .items-header-count {
    display: flex;
    min-width: 74px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 9px 13px;
    border-radius: 13px;
    background: #ecfdf5;
  }

  .items-header-count strong {
    color: #047857;
    font-size: 20px;
    line-height: 1;
  }

  .items-header-count span {
    margin-top: 4px;
    color: #47816f;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* ========================================================
     LOADING
  ======================================================== */

  .items-loading {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 11px;
    color: #94a3b8;
    font-size: 11px;
  }

  .items-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #d1fae5;
    border-top-color: #047857;
    border-radius: 50%;
    animation:
      items-spin
      0.8s
      linear
      infinite;
  }

  /* ========================================================
     TOOLBAR
  ======================================================== */

  .items-toolbar {
    display: flex;
    align-items: center;
    justify-content:
      space-between;
    gap: 16px;
    margin-bottom: 16px;
    padding: 13px 14px;
    border: 1px solid #e7e5df;
    border-radius: 13px;
    background: #fbf7ed;
  }

  .items-toolbar strong {
    display: block;
    margin-bottom: 2px;
    color: #334155;
    font-size: 11px;
  }

  .items-toolbar span {
    display: block;
    color: #64748b;
    font-size: 9px;
  }

  .items-toolbar-buttons {
    display: flex;
    gap: 7px;
  }

  .items-toolbar-buttons button {
    min-height: 32px;
    padding: 7px 10px;
    border: 1px solid #d2dfda;
    border-radius: 9px;
    background: #ffffff;
    color: #047857;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .items-toolbar-buttons button:hover:not(:disabled) {
    background: #f0fdf4;
  }

  /* ========================================================
     GRID
  ======================================================== */

  .items-grid {
    display: grid;
    grid-template-columns:
      repeat(
        auto-fill,
        minmax(165px, 1fr)
      );
    gap: 10px;
  }

  .item-card {
    display: flex;
    min-height: 65px;
    align-items: center;
    gap: 11px;
    padding: 11px 12px;
    border: 1px solid #e2e8e5;
    border-radius: 13px;
    background: #ffffff;
    color: #475569;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition:
      border-color 0.18s ease,
      background 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
  }

  .item-card:hover:not(:disabled) {
    border-color: #b7d9cd;
    background: #fcfffd;
    transform: translateY(-1px);
    box-shadow:
      0 5px 16px
      rgba(
        15,
        23,
        42,
        0.045
      );
  }

  .item-card-active {
    border-color: #8fcdb7;
    background: #f0fdf4;
  }

  .item-card-check {
    display: grid;
    width: 26px;
    height: 26px;
    flex: 0 0 auto;
    place-items: center;
    border: 1px solid #d8dfdc;
    border-radius: 8px;
    background: #f8faf9;
    color: #ffffff;
  }

  .item-card-active
  .item-card-check {
    border-color: #047857;
    background: #047857;
  }

  .item-card-check span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #cbd5e1;
  }

  .item-card-copy {
    min-width: 0;
    flex: 1;
  }

  .item-card-title-line {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
  }

  .item-card-copy strong {
    display: block;
    overflow: hidden;
    color: #334155;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-card-active
  .item-card-copy strong {
    color: #047857;
  }

  .item-card-copy > span {
    display: block;
    margin-top: 2px;
    color: #94a3b8;
    font-size: 8px;
  }

  .item-card-active
  .item-card-copy > span {
    color: #5a9984;
  }

  .item-custom-badge {
    display: inline-flex !important;
    flex: 0 0 auto;
    margin: 0 !important;
    padding: 2px 5px;
    border-radius: 999px;
    background: #f1ead9;
    color: #82735a !important;
    font-size: 6px !important;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* ========================================================
     CUSTOM CATEGORY
  ======================================================== */

  .items-custom-section {
    position: relative;
    margin-top: 18px;
    padding: 16px;
    border: 1px solid #e3dccd;
    border-radius: 15px;
    background:
      linear-gradient(
        135deg,
        #fcf8ef 0%,
        #f8f2e5 100%
      );
  }

  .items-custom-copy {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 13px;
  }

  .items-custom-icon {
    display: grid;
    width: 34px;
    height: 34px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 10px;
    background: #e9f4ef;
    color: #047857;
  }

  .items-custom-copy strong {
    display: block;
    color: #4b453a;
    font-size: 10px;
  }

  .items-custom-copy span {
    display: block;
    margin-top: 2px;
    color: #8a806f;
    font-size: 8px;
    line-height: 1.45;
  }

  .items-custom-form {
    display: flex;
    align-items: stretch;
    gap: 8px;
  }

  .items-custom-form input {
    box-sizing: border-box;
    min-width: 0;
    min-height: 39px;
    flex: 1;
    padding: 9px 11px;
    border: 1px solid #d6cebe;
    border-radius: 10px;
    outline: none;
    background: #ffffff;
    color: #334155;
    font-family: inherit;
    font-size: 10px;
    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease;
  }

  .items-custom-form input:focus {
    border-color: #7db7a2;
    box-shadow:
      0 0 0 3px
      rgba(
        4,
        120,
        87,
        0.08
      );
  }

  .items-custom-form input::placeholder {
    color: #aaa292;
  }

  .items-custom-form button {
    display: inline-flex;
    min-width: 74px;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 8px 12px;
    border: 0;
    border-radius: 10px;
    background: #047857;
    color: #ffffff;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .items-custom-form button:hover:not(:disabled) {
    background: #036b4f;
  }

  .items-custom-form button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .items-custom-limit {
    margin-top: 6px;
    color: #a39b8b;
    font-size: 7px;
    text-align: right;
  }

  /* ========================================================
     CUSTOM TAGS
  ======================================================== */

  .items-custom-list {
    margin-top: 12px;
    padding: 13px 14px;
    border: 1px solid #ece8df;
    border-radius: 13px;
    background: #fffdfa;
  }

  .items-custom-list-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 9px;
  }

  .items-custom-list-heading strong {
    color: #625b4e;
    font-size: 9px;
  }

  .items-custom-list-heading > span {
    display: grid;
    min-width: 21px;
    height: 21px;
    place-items: center;
    border-radius: 999px;
    background: #f2ecdf;
    color: #7b705d;
    font-size: 7px;
    font-weight: 800;
  }

  .items-custom-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .items-custom-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 6px 5px 9px;
    border: 1px solid #ddd4c4;
    border-radius: 999px;
    background: #fbf7ed;
    color: #655c4d;
    font-size: 8px;
    font-weight: 700;
  }

  .items-custom-tag button {
    display: grid;
    width: 18px;
    height: 18px;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #948b7b;
    cursor: pointer;
  }

  .items-custom-tag button:hover:not(:disabled) {
    background: #ede6d8;
    color: #6f6251;
  }

  /* ========================================================
     INFO
  ======================================================== */

  .items-tip {
    display: flex;
    gap: 10px;
    margin-top: 17px;
    padding: 12px 13px;
    border-radius: 12px;
    background: #f8faf9;
  }

  .items-tip-icon {
    display: grid;
    width: 31px;
    height: 31px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 9px;
    background: #ecfdf5;
    color: #047857;
  }

  .items-tip strong {
    display: block;
    margin-bottom: 2px;
    color: #475569;
    font-size: 10px;
  }

  .items-tip span {
    display: block;
    color: #94a3b8;
    font-size: 9px;
    line-height: 1.5;
  }

  /* ========================================================
     ACTIONS
  ======================================================== */

  .items-actions {
    display: flex;
    align-items: center;
    justify-content:
      space-between;
    gap: 18px;
    margin-top: 18px;
    padding-top: 17px;
    border-top: 1px solid #eef2f0;
  }

  .items-change-state {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #64748b;
    font-size: 9px;
    font-weight: 700;
  }

  .items-change-dot,
  .items-saved-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  .items-change-dot {
    background: #f59e0b;
  }

  .items-saved-dot {
    background: #10b981;
  }

  .items-action-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .items-reset-button,
  .items-save-button {
    display: inline-flex;
    min-height: 39px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 13px;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
  }

  .items-reset-button {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #64748b;
  }

  .items-reset-button:hover:not(:disabled) {
    background: #f8fafc;
  }

  .items-save-button {
    border: none;
    background: #047857;
    color: #ffffff;
    box-shadow:
      0 7px 18px
      rgba(
        4,
        120,
        87,
        0.16
      );
  }

  .items-save-button:hover:not(:disabled) {
    background: #036b4f;
  }

  .item-card:disabled,
  .items-toolbar-buttons button:disabled,
  .items-reset-button:disabled,
  .items-save-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .items-button-spinner {
    width: 11px;
    height: 11px;
    border: 2px solid
      rgba(
        255,
        255,
        255,
        0.4
      );
    border-top-color: #ffffff;
    border-radius: 50%;
    animation:
      items-spin
      0.7s
      linear
      infinite;
  }

  @keyframes items-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ========================================================
     RESPONSIVE
  ======================================================== */

  @media (max-width: 650px) {

    .items-toolbar {
      align-items: stretch;
      flex-direction: column;
    }

    .items-toolbar-buttons {
      justify-content: flex-start;
    }

    .items-grid {
      grid-template-columns:
        repeat(
          2,
          minmax(0, 1fr)
        );
    }

    .items-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .items-action-buttons {
      width: 100%;
    }

    .items-reset-button,
    .items-save-button {
      flex: 1;
    }

  }

  @media (max-width: 430px) {

    .items-panel {
      padding: 17px;
      border-radius: 17px;
    }

    .items-grid {
      grid-template-columns: 1fr;
    }

    .items-custom-form {
      flex-direction: column;
    }

    .items-custom-form button {
      width: 100%;
    }

    .items-action-buttons {
      flex-direction: column;
    }

    .items-reset-button,
    .items-save-button {
      width: 100%;
    }

  }

`;